import { useParams, Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [featuredHovered, setFeaturedHovered] = useState<number | null>(0);

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);
  const featuredArticles = categoryArticles.slice(0, 3);
  const otherCategories = categories.filter((c) => c.slug !== slug);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类不存在</h1>
          <Link to="/" className="text-accent-500 hover:text-accent-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `51爆料网 - ${category.name}`,
    description: category.description,
    url: `http://blw51.cn/category/${slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[360px] md:h-[480px] flex items-center justify-center overflow-hidden">
            <img
              src={category.heroImage}
              alt={`51爆料网 - ${category.name}`}
              title={`51爆料网${category.name}分类`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/65"></div>
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <p className="text-sm text-background-300 mb-2">
                <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
                <span className="mx-2">/</span>
                <span className="text-background-50">{category.name}</span>
              </p>
              <h1 className="text-3xl md:text-5xl font-black text-background-50 leading-tight">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-background-200 mt-3 max-w-xl mx-auto">
                {category.description}
              </p>
            </div>
          </section>

          {categoryArticles.length > 0 && (
            <section className="py-14 md:py-16 px-4 md:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                    {category.name} · 最新内容
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">实时更新 · 黑料不打烊</p>
                </div>
              </div>

              <div className="relative">
                <button
                  onClick={scrollLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100 hover:border-background-300 transition-all"
                >
                  <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>

                <div
                  ref={scrollRef}
                  className="flex gap-5 overflow-x-auto scrollbar-hide pb-2 px-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      id={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      category={article.category}
                      categorySlug={article.categorySlug}
                      hotIndex={article.hotIndex}
                      readCount={article.readCount}
                      imageUrl={article.imageUrl}
                    />
                  ))}
                </div>

                <button
                  onClick={scrollRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100 hover:border-background-300 transition-all"
                >
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </section>
          )}

          {featuredArticles.length > 0 && (
            <section className="py-14 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100/50">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                精选{category.name}爆料
              </h2>
              <p className="text-sm text-foreground-500 mb-8">深度追踪，还原事件真相</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {featuredArticles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group relative rounded-lg overflow-hidden transition-all duration-500 no-underline"
                    style={{ height: '420px' }}
                    onMouseEnter={() => setFeaturedHovered(index)}
                    onMouseLeave={() => setFeaturedHovered(0)}
                  >
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                        featuredHovered === index ? 'scale-105' : 'scale-100'
                      }`}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <span className="inline-block px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50 mb-3 whitespace-nowrap">
                        {article.category}
                      </span>
                      <h3 className="text-lg md:text-xl font-bold text-background-50 leading-snug mb-2">
                        {article.title}
                      </h3>
                      {(featuredHovered === index || index === 0) && (
                        <p className="text-sm text-background-200 leading-relaxed line-clamp-3 transition-all duration-300">
                          {article.excerpt}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-xs text-background-300">
                          <i className="ri-eye-line mr-1 w-3 h-3 inline-flex items-center justify-center"></i>
                          {article.readCount}
                        </span>
                        <span className="text-xs text-background-300">
                          <i className="ri-time-line mr-1 w-3 h-3 inline-flex items-center justify-center"></i>
                          {article.publishDate}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="py-14 md:py-16 px-4 md:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">其他精彩栏目</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {otherCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all duration-200 no-underline group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{cat.name}</p>
                    <p className="text-xs text-foreground-500">{cat.description.slice(0, 12)}...</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}